import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { Layout } from './components/Layout'
import { CookieConsentBanner } from './components/CookieConsentBanner'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { TenantsPage } from './pages/TenantsPage'
import { TenantDetailPage } from './pages/TenantDetailPage'
import { TenantEditPage } from './pages/TenantEditPage'
import { SubscriptionTiersPage } from './pages/SubscriptionTiersPage'
import { PoliciesPage } from './pages/PoliciesPage'
import { SupportAnalysisPage } from './pages/SupportAnalysisPage'
import { PublicFormActivityPage } from './pages/PublicFormActivityPage'
import { ActivityAnalyticsPage } from './pages/ActivityAnalyticsPage'
import { SystemProxiesPage } from './pages/SystemProxiesPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { SettingsPage } from './pages/SettingsPage'
import { AdminUsersPage } from './pages/AdminUsersPage'
import { ChangePasswordPage } from './pages/ChangePasswordPage'

// Protected Route component. `ownerOnly` routes send staff back to the dashboard;
// an account still on its temporary password can only reach the change-password page.
const ProtectedRoute = ({ children, ownerOnly = false, allowPendingPasswordChange = false }) => {
  const { isAuthenticated, loading, isOwner, mustChangePassword } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-admin-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-admin-600">Carregando...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (mustChangePassword && !allowPendingPasswordChange) return <Navigate to="/account/password" replace />
  if (ownerOnly && !isOwner) return <Navigate to="/" replace />
  return children
}

// Public Route component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-admin-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p className="text-admin-300">Carregando...</p>
        </div>
      </div>
    )
  }

  return isAuthenticated ? <Navigate to="/" replace /> : children
}

// Placeholder components for other pages

const AnalyticsPage = () => <ActivityAnalyticsPage />

const SystemPage = () => <SystemProxiesPage />

// Main App Router
const AppRouter = () => {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        
        {/* Protected routes */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tenants" 
          element={
            <ProtectedRoute>
              <Layout>
                <TenantsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tenants/:id" 
          element={
            <ProtectedRoute>
              <Layout>
                <TenantDetailPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tenants/:id/edit" 
          element={
            <ProtectedRoute ownerOnly>
              <Layout>
                <TenantEditPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/subscription-tiers" 
          element={
            <ProtectedRoute>
              <Layout>
                <SubscriptionTiersPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/policies" 
          element={
            <ProtectedRoute>
              <Layout>
                <PoliciesPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/support-analysis" 
          element={
            <ProtectedRoute>
              <Layout>
                <SupportAnalysisPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/public-forms" 
          element={
            <ProtectedRoute ownerOnly>
              <Layout>
                <PublicFormActivityPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/analytics" 
          element={
            <ProtectedRoute ownerOnly>
              <Layout>
                <AnalyticsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/users" 
          element={
            <ProtectedRoute ownerOnly>
              <Layout>
                <AdminUsersPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/system" 
          element={
            <ProtectedRoute>
              <Layout>
                <SystemPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Layout>
                <NotificationsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <Layout>
                <SettingsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/account/password"
          element={
            <ProtectedRoute allowPendingPasswordChange>
              <Layout>
                <ChangePasswordPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}

// Main App component
function App() {
  return (
    <AuthProvider>
      <AppRouter />
      <CookieConsentBanner />
    </AuthProvider>
  )
}

export default App 